// AdminMoney — the manual-payment approval queue, redesigned to match
// the new dashboard aesthetic.
//
// Three queue types, each at its own URL so the dashboard's deep
// links work and the URL is bookmarkable / shareable:
//
//   /admin/money/recharges          → player recharge requests
//   /admin/money/withdrawals        → player withdrawal requests
//   /admin/money/agent-withdrawals  → agent withdrawal requests (admin → agent payout)
//   /admin/money                    → defaults to recharges
//
// Each page has the same shell:
//   • Top entity-themed tab pills (Recharges / Withdrawals / Agent w'als)
//   • Pending / History sub-tabs (with live counts on the Pending pill)
//   • Mobile-first card list — each request is a card with avatar,
//     amount + green/red flow arrow, request details, and action
//     buttons (Approve / Reject / Mark Paid). Tables don't work on
//     phones; cards do.
//
// Light + dark designed. Honours the same range conventions used
// elsewhere in the admin UI.

import { ReactNode, useCallback, useEffect, useState } from 'react';
import { levelName } from '../../lib/levelNames';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
import ApprovalEditModal from '../../components/ApprovalEditModal';
import DirectAdjustCard from '../../components/DirectAdjustCard';
import ProofImageViewer, { type ProofState } from '@baaligames/shared-ui/components/ProofImageViewer';
import ProofImagePicker from '@baaligames/shared-ui/components/ProofImagePicker';
import ProofImagePickerChip from '@baaligames/shared-ui/components/ProofImagePickerChip';

// ─── Types ─────────────────────────────────────────────────────────

type Kind = 'recharge' | 'withdrawal' | 'agent-recharge' | 'agent-withdrawal';
// 2026-05-18 — Three-tab queue overhaul (Phase C1):
//   direct  = pending requests from / involving L1 agents (admin's direct
//             downline). Sorted OLDEST first so no one waits forever.
//   others  = pending requests from / involving L2+ agents (deeper chain).
//             Also oldest first.
//   history = terminal-state requests across all levels.
//             Sorted NEWEST first so admins see fresh decisions first.
type Sub  = 'direct' | 'others' | 'history';

// Phase 18 — payment-proof columns on every row type. The list
// endpoints SELECT rr.* / wr.* / r.* / w.*, so these flow through
// automatically. The frontend computes the proof state inline from
// the *_uploaded_at timestamps.
type ProofColumns = {
  proof_image_url: string | null;
  proof_uploaded_at: string | null;
  payout_proof_url: string | null;
  payout_proof_uploaded_at: string | null;
  payout_proof_view_count: number;
};
type RechargeRow = ProofColumns & {
  id: number;
  user_id: number;
  user_username: string;
  user_email: string;
  user_phone: string;
  user_first_name: string | null;
  user_last_name:  string | null;
  amount: number;
  transaction_id: string;
  user_note: string | null;
  state: 'pending'|'approved'|'rejected'|'expired';
  decided_by_admin_id: number | null;
  decided_by_agent_id: number | null;
  decided_at: string | null;
  decision_note: string | null;
  ledger_id: number | null;
  attached_offer_claim_id: number | null;
  created_at: string;
  // Routing — populated by RechargeRequestModel.listForAdmin via
  // JOIN agents (post 2026-05-18 every player has an agent). The
  // owning agent's level powers the Direct/Others split.
  target_admin_id: number | null;
  target_agent_id: number | null;
  target_agent_username:   string | null;
  target_agent_first_name: string | null;
  target_agent_last_name:  string | null;
  target_agent_level:      number | null;
  target_agent_uaid: string | null;
};

type WithdrawalRow = ProofColumns & {
  id: number;
  user_id: number;
  user_username: string;
  user_phone: string;
  user_first_name: string | null;
  user_last_name:  string | null;
  amount: number;
  upi_id: string;
  whatsapp_number: string | null;
  qr_image_url: string | null;
  state: 'pending'|'approved'|'rejected'|'paid'|'expired';
  decided_by_admin_id: number | null;
  decided_by_agent_id: number | null;
  decided_at: string | null;
  decision_note: string | null;
  paid_transaction_id: string | null;
  paid_at: string | null;
  hold_ledger_id: number | null;
  created_at: string;
  target_admin_id: number | null;
  target_agent_id: number | null;
  target_agent_username:   string | null;
  target_agent_first_name: string | null;
  target_agent_last_name:  string | null;
  target_agent_level:      number | null;
  target_agent_uaid: string | null;
};

type AgentWithdrawalRow = ProofColumns & {
  id: number;
  agent_id: number;
  agent_username: string;
  agent_first_name: string | null;
  agent_last_name:  string | null;
  agent_level:      number | null;
  agent_avatar_url: string | null;
  agent_email: string;
  agent_phone: string | null;
  amount: number;
  upi_id: string;
  whatsapp_number: string | null;
  agent_note: string | null;
  state: 'pending'|'approved'|'rejected'|'paid'|'expired';
  decided_at: string | null;
  decision_note: string | null;
  paid_transaction_id: string | null;
  paid_at: string | null;
  hold_ledger_id: number | null;
  created_at: string;
};

type AgentRechargeRow = ProofColumns & {
  id: number;
  agent_id: number;
  agent_username: string;
  agent_first_name: string | null;
  agent_last_name:  string | null;
  agent_level:      number | null;
  agent_avatar_url: string | null;
  agent_email: string;
  agent_phone: string | null;
  amount: number;
  transaction_id: string;
  agent_note: string | null;
  state: 'pending'|'approved'|'rejected'|'expired';
  decided_at: string | null;
  decision_note: string | null;
  ledger_id: number | null;
  created_at: string;
};

// 2026-05-18 — Per-tab pending counts, shape returned by the batched
// /admin/payment-requests/counts endpoint. Replaces four separate
// ?state=pending&limit=1 polls. Each entry has .direct (L1 requests)
// and .others (L2+ requests) pending counters; the total pending for
// any kind = direct + others.
type CountsBlock = { direct: number; others: number };
type CountsBundle = {
  player_recharge:   CountsBlock;
  player_withdrawal: CountsBlock;
  agent_recharge:    CountsBlock;
  agent_withdrawal:  CountsBlock;
};
const ZERO_COUNTS: CountsBundle = {
  player_recharge:   { direct: 0, others: 0 },
  player_withdrawal: { direct: 0, others: 0 },
  agent_recharge:    { direct: 0, others: 0 },
  agent_withdrawal:  { direct: 0, others: 0 },
};

// Build a peer-friendly display label from first/last with a dash
// placeholder when both are missing. Username never falls through —
// 2026-05-18 identity-masking directive (admin UI keeps @username as
// a SECONDARY id alongside the display name, but the display name is
// always primary).
function displayName(first: string | null | undefined, last: string | null | undefined): string {
  const f = (first || '').trim();
  const l = (last  || '').trim();
  if (f && l) return `${f} ${l}`;
  if (f) return f;
  if (l) return l;
  return '—';
}

const LIMIT = 50;

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

// ─── Phase 18 — proof-state helpers ───────────────────────────────
//
// Both sender_proof_state and payout_proof_state are derived inline
// from the *_uploaded_at timestamps + the URL columns. The 72h
// expiry math is done client-side from `Date.now()` so the badge
// flips to "expired" the moment it crosses the line, even if the
// row was loaded before the cron sweep ran.
const VIEW_EXPIRY_MS    = 72 * 60 * 60 * 1000;
const UPLOAD_WINDOW_MS  = 24 * 60 * 60 * 1000;
function proofStateFor(uploadedAt: string | null, url: string | null): ProofState {
  if (!uploadedAt) return 'none';
  const age = Date.now() - new Date(uploadedAt).getTime();
  if (age > VIEW_EXPIRY_MS) return 'expired';
  return url ? 'available' : 'expired';
}
function canUploadPayoutProof(paidAt: string | null, state: string, hasProof: boolean): boolean {
  if (hasProof) return false;
  if (state !== 'paid') return false;
  if (!paidAt) return false;
  return (Date.now() - new Date(paidAt).getTime()) < UPLOAD_WINDOW_MS;
}

/**
 * PayoutProofCell — drop-in chip for withdrawal rows. Renders one of:
 *   - nothing (state !== 'paid')
 *   - the viewer (payout_proof_url already set)
 *   - an upload picker (within 24h of paid_at, no proof yet)
 *   - "Upload window closed" pill (past 24h, no proof)
 *
 * `uploadEndpoint` and `viewEndpoint` differ per scope (admin vs
 * parent vs agent-withdrawal); the parent passes the right paths.
 */
function PayoutProofCell({
  row, uploadEndpoint, viewEndpoint, onUploaded,
}: {
  row: { state: string; paid_at: string | null;
         payout_proof_url: string | null; payout_proof_uploaded_at: string | null };
  uploadEndpoint: string;
  viewEndpoint: string;
  onUploaded?: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (row.state !== 'paid') return null;

  const proofState = proofStateFor(row.payout_proof_uploaded_at, row.payout_proof_url);
  if (proofState === 'available' || proofState === 'expired') {
    return (
      <ProofImageViewer state={proofState} endpoint={viewEndpoint}
                        apiGet={(p) => adminApi.get(p)} />
    );
  }

  const can = canUploadPayoutProof(row.paid_at, row.state, false);
  if (!can) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold
                       bg-arena-100/70 text-arena-500 dark:text-neon-200/60 ring-1 ring-inset ring-arena-200/50
                       dark:bg-arena-700/40 dark:text-neon-200/55 dark:ring-arena-600/40">
        Upload window closed
      </span>
    );
  }

  async function send(blob: Blob) {
    setUploading(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('proof', blob, 'payout.jpg');
      await adminApi.postForm(uploadEndpoint, fd);
      onUploaded?.();
    } catch (e: any) {
      setErr(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    // Compact chip — keeps the card's narrow right column compact so
    // the content column doesn't get squeezed on small screens.
    <span className="inline-flex items-center gap-2 flex-wrap justify-end">
      <ProofImagePickerChip
        onFile={(b) => send(b)}
        onError={(m) => setErr(m)}
        disabled={uploading}
      />
      {err && <span className="text-[11px] text-rose-700 dark:text-rose-300">{err}</span>}
    </span>
  );
}
const fmtDate = (s: string) => new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

// ─── URL → scope resolver (Phase 16.10) ───────────────────────────
//
// Scope is players-or-agents; direction is in-or-out. We split them
// because the FinanceHub now offers ONE tab per scope and the
// in/out toggle lives inside the panel as internal state.

type Scope = 'players' | 'agents';

function scopeFromPath(pathname: string): Scope {
  if (pathname.includes('/agents'))  return 'agents';
  // Legacy URLs (agent-recharge, agent-withdraw...) still mean agents
  if (pathname.includes('agent-recharge') || pathname.includes('agent-withdraw')) return 'agents';
  return 'players';
}

// Map (scope, direction) → the legacy Kind so the existing Queue /
// SubTabs / Header components keep working unchanged.
function kindFor(scope: Scope, dir: 'in' | 'out'): Kind {
  if (scope === 'agents') return dir === 'in' ? 'agent-recharge' : 'agent-withdrawal';
  return dir === 'in' ? 'recharge' : 'withdrawal';
}

// ─── Top-level page ────────────────────────────────────────────────

export default function AdminMoneyPanel() {
  const can = useAdminAuth().canDo;
  const loc = useLocation();
  const scope = scopeFromPath(loc.pathname);

  // Phase 16.10 — direction is INTERNAL state, not URL.
  // Default landing direction is "in" (recharges) to match the previous
  // behavior of /finance/players → /finance/players/in.
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const kind = kindFor(scope, direction);
  // 2026-05-18 — Default landing sub-tab is "direct" so admin lands
  // on their L1 agents' pending queue first (the most urgent group
  // since L1 are the platform's direct downline).
  const [sub, setSub] = useState<Sub>('direct');

  // 2026-05-18 — Single batched counts endpoint replaces four
  // per-queue ?state=pending&limit=1 polls. Returns {direct, others}
  // pending counts for all four queues in one request.
  const [counts, setCounts] = useState<CountsBundle>(ZERO_COUNTS);

  const refreshCounts = useCallback(async () => {
    try {
      const r = await adminApi.get<CountsBundle>('/admin/payment-requests/counts');
      setCounts(r);
    } catch { /* surface in main view */ }
  }, []);
  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  // Reset direction + sub-tab when scope route changes (only fires on
  // a true scope navigation, e.g. /finance/players → /finance/agents,
  // not on the in/out toggle which is now internal state).
  useEffect(() => {
    setDirection('in');
    setSub('direct');
  }, [scope]);

  // Helper: per-kind {direct, others} block from the bundle.
  function blockFor(k: Kind): CountsBlock {
    return k === 'recharge'         ? counts.player_recharge
         : k === 'withdrawal'       ? counts.player_withdrawal
         : k === 'agent-recharge'   ? counts.agent_recharge
         :                            counts.agent_withdrawal;
  }
  const kindBlock = blockFor(kind);
  // Inbound / outbound counts shown on the in/out direction toggle
  // are "total pending" = direct + others for the relevant queue.
  const inboundBlock  = blockFor(scope === 'agents' ? 'agent-recharge'   : 'recharge');
  const outboundBlock = blockFor(scope === 'agents' ? 'agent-withdrawal' : 'withdrawal');
  const inboundCount  = inboundBlock.direct + inboundBlock.others;
  const outboundCount = outboundBlock.direct + outboundBlock.others;

  return (
    // Phase 3 (2026-05-15): AdminMoneyPanel is now ALWAYS embedded
    // inside a hub (PlayerRequestsPanel / AgentRequestsPanel), which
    // already provides the max-w wrapper, page-level header
    // ("Players" / "Agents"), and outer padding. So we drop:
    //   • the outer `max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4`
    //     wrapper (the hub owns the gutter)
    //   • the <Header kind={kind} /> section ("Player money in" / blurb)
    //     which duplicated context the hub header + DirectionStateToggle
    //     already provide ("Players" header above + "Player money in / out"
    //     toggle below)
    // The Header function is kept defined-but-unused for now in case a
    // future surface wants the standalone look back.
    <div className="space-y-4">
      <DirectionStateToggle
        active={direction}
        onChange={setDirection}
        inCount={inboundCount}
        outCount={outboundCount}
        isAgent={scope === 'agents'}
      />
      {/* 2026-05-16 — "Direct {recharge|withdraw}" search card sits
          above the queue on every direction × scope combination. It
          gives admin the no-request-needed path (cash collected in
          person, claw-back of mistaken credit, agent settlement, etc).
          The card itself is read-only: picking a row opens the
          existing DirectWalletAdjustModal pre-loaded with the right
          subject + direction. Refresh hooks back into refreshCounts
          so the pending pill stays in sync if a credit/debit
          influences the queue. */}
      <div className="px-2 lg:px-4">
        <DirectAdjustCard
          scope={scope}
          direction={direction}
          onAfterAdjust={refreshCounts}
        />
      </div>
      <SubTabs sub={sub} onChange={setSub} kind={kind} counts={kindBlock} />
      <Queue kind={kind} sub={sub} onChanged={refreshCounts} />
    </div>
  );
}

// Phase 16.10 — state-driven in/out toggle. Replaces the old
// Link-based DirectionToggle which forced a route change. This one
// just flips a useState and the panel stays mounted.
function DirectionStateToggle({ active, onChange, inCount, outCount, isAgent }: {
  active: 'in' | 'out';
  onChange: (d: 'in' | 'out') => void;
  inCount: number; outCount: number;
  isAgent: boolean;
}) {
  return (
    <div className="px-2 lg:px-4">
      <div className="grid grid-cols-2 gap-2">
        <DirStateBtn dir="in"  active={active === 'in'}  onClick={() => onChange('in')}  count={inCount}  isAgent={isAgent} />
        <DirStateBtn dir="out" active={active === 'out'} onClick={() => onChange('out')} count={outCount} isAgent={isAgent} />
      </div>
    </div>
  );
}

function DirStateBtn({ dir, active, onClick, count, isAgent }: {
  dir: 'in' | 'out';
  active: boolean;
  onClick: () => void;
  count: number;
  // Kept on the signature for compatibility with the parent's call
  // site, even though the labels are now identical for both scopes —
  // the surrounding hub header already tells the admin whether they
  // are looking at the Players or Agents queue, so duplicating that
  // word on every toggle button just adds noise.
  isAgent: boolean;
}) {
  // 2026-05-16: simplified labels. Was "Player money in" / "Agent
  // money out" etc — redundant with the hub-level "Players" / "Agents"
  // title already shown above. The direction toggle now reads as the
  // verb only ("Recharge" / "Withdrawal") matching the data the queue
  // actually holds.
  void isAgent;
  const label = dir === 'in' ? 'Recharge' : 'Withdrawal';
  const icon = dir === 'in' ? 'arrow_upward' : 'arrow_downward';

  const activeCls = dir === 'in'
    ? 'bg-emerald-600 text-white border-emerald-600 shadow-[0_2px_10px_rgba(16,185,129,0.4)]'
    : 'bg-rose-600 text-white border-rose-600 shadow-[0_2px_10px_rgba(244,63,94,0.4)]';
  const inactiveCls = `bg-white text-arena-700 dark:text-neon-200/80 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50
                       dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40 dark:hover:bg-arena-700/60`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5
        text-[13px] font-bold border transition active:scale-95
        ${active ? activeCls : inactiveCls}
      `}
    >
      <Icon name={icon} size={15} />
      <span className="truncate">{label}</span>
      {count > 0 && (
        <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10.5px] font-bold tabular-nums
          ${active
            ? 'bg-white/25 text-white'
            : 'bg-amber-100 text-amber-800 dark:bg-amber-400/20 dark:text-amber-200'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

// Phase 16.10: BackBar / DirectionToggle / DirBtn removed.
// BackBar pointed to /money which no longer exists as a hub root —
// the new collapsed FinanceHub is accessed via its own tab.
// DirectionToggle + DirBtn navigated between /in and /out routes;
// they're replaced by DirectionStateToggle + DirStateBtn defined
// inline above the panel render (no navigation, just useState).

// ─── Header ────────────────────────────────────────────────────────

function Header({ kind }: { kind: Kind }) {
  // 2026-05-16: short titles. The hub-level header already says
  // whether we're on the Players or Agents queue; this banner just
  // names the direction. Subtitles still mention the entity for the
  // standalone surface (legacy /finance/* paths that don't have a
  // hub header above them).
  const META: Record<Kind, { title: string; subtitle: string; icon: string }> = {
    'recharge':         { title: 'Recharge',   subtitle: 'Approve player wallet top-ups · paid manually via UPI', icon: 'south_west' },
    'withdrawal':       { title: 'Withdrawal', subtitle: 'Pay players via UPI and mark each request paid',         icon: 'north_east' },
    'agent-recharge':   { title: 'Recharge',   subtitle: 'Approve agent pool top-ups · agent paid you via UPI',    icon: 'south_west' },
    'agent-withdrawal': { title: 'Withdrawal', subtitle: 'Pay agents from their pool · admin-only queue',          icon: 'north_east' },
  };
  const m = META[kind];
  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                className="px-2 lg:px-4 pt-3 lg:pt-2">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-xl
                         bg-violet-50 text-violet-600 ring-1 ring-violet-100
                         dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
          <Icon name={m.icon} size={22} filled />
        </span>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-neutral-900 dark:text-white tracking-tight">
            {m.title}
          </h1>
          <p className="text-[12px] text-arena-600 dark:text-neon-200/70 truncate">{m.subtitle}</p>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Top tabs (queue selector) ─────────────────────────────────────

// (KindTabs removed — replaced by AdminMoneyHub at /admin/money where
// the 4 queues are big colored cards instead of horizontally-scrolling
// pill tabs above every queue page.)

// ─── Direct / Others / History sub-tabs ────────────────────────────
//
// 2026-05-18 (Three-tab queue overhaul):
//   • Direct  — pending requests involving L1 agents (admin's direct
//               downline). Oldest first. Per-tab count badge.
//   • Others  — pending requests involving L2+ agents. Oldest first.
//               Per-tab count badge.
//   • History — terminal-state requests, all levels, newest first.
//               No count badge (history is unbounded by design).
//
// The labels include a short subtitle hint inline on wider screens
// ("L1 agents" / "L2+ agents") so admins don't have to memorise the
// meaning of "direct vs others" — the tab self-documents.

function SubTabs({ sub, onChange, kind, counts }: {
  sub: Sub; onChange: (s: Sub) => void; kind: Kind; counts: CountsBlock;
}) {
  const TABS: Array<{
    key: Sub;
    label: string;
    sub: string;            // tiny inline subtitle ("L1 agents", etc.)
    count: number | null;   // null = no badge
    icon: string;
  }> = [
    { key: 'direct',  label: 'Direct',  sub: 'Super Masters',    count: counts.direct,  icon: 'looks_one' },
    { key: 'others',  label: 'Others',  sub: 'Masters & Agents',   count: counts.others,  icon: 'group_work' },
    { key: 'history', label: 'History', sub: 'all',   count: null,           icon: 'history' },
  ];
  return (
    <div className="px-2 lg:px-4 flex items-center justify-between gap-2 flex-wrap">
      <div className="inline-flex rounded-full p-1
                      bg-arena-100/60 dark:bg-arena-800/40
                      ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
        {TABS.map((t) => {
          const active = t.key === sub;
          const showCount = t.count != null && t.count > 0;
          return (
            <button
              key={t.key}
              onClick={() => onChange(t.key)}
              title={
                t.key === 'direct'
                  ? 'Pending requests involving L1 (direct) agents — oldest first'
                  : t.key === 'others'
                  ? 'Pending requests involving Masters & Agents — oldest first'
                  : 'Terminal-state requests across all levels — newest first'
              }
              className={`px-3 sm:px-3.5 py-1.5 rounded-full text-[12px] font-semibold capitalize transition flex items-center gap-1.5
                ${active
                  ? 'bg-violet-600 text-white dark:bg-white dark:text-neutral-900 shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
                  : 'text-arena-700 hover:text-arena-900 dark:text-neon-200/70 dark:hover:text-white'}`}
            >
              <Icon name={t.icon} size={13} />
              {t.label}
              <span className={`hidden sm:inline text-[10px] font-medium opacity-75 -ml-0.5`}>· {t.sub}</span>
              {showCount && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold tabular-nums
                  ${active ? 'bg-white/25 dark:bg-arena-900/30' : 'bg-amber-100 text-amber-800 dark:bg-amber-400/20 dark:text-amber-200'}`}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Batch1 fix #16: route + label by SCOPE, not just kind. */}
      <Link
        to={(kind === 'agent-recharge' || kind === 'agent-withdrawal') ? '/people/agents' : '/people/players'}
        className="text-[12px] text-violet-700 dark:text-violet-300 hover:underline font-semibold inline-flex items-center gap-1"
      >
        <Icon name="arrow_forward" size={12} />
        See all {(kind === 'agent-recharge' || kind === 'agent-withdrawal') ? 'agents' : 'players'}
      </Link>
    </div>
  );
}

// ─── Queue body ────────────────────────────────────────────────────

function Queue({ kind, sub, onChanged }: { kind: Kind; sub: Sub; onChanged: () => void }) {
  const [rRows, setRRows]   = useState<RechargeRow[]>([]);
  const [wRows, setWRows]   = useState<WithdrawalRow[]>([]);
  const [aRows, setARows]   = useState<AgentWithdrawalRow[]>([]);
  const [arRows, setArRows] = useState<AgentRechargeRow[]>([]);
  const [total, setTotal]   = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const endpoint = kind === 'recharge'         ? '/admin/payment-requests/recharge'
                 : kind === 'withdrawal'       ? '/admin/payment-requests/withdrawal'
                 : kind === 'agent-recharge'   ? '/admin/payment-requests/agent-recharge'
                 :                                '/admin/payment-requests/agent-withdrawal';

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      // 2026-05-18 — One request per tab regardless of state. The
      // backend now handles the multi-state UNION for history (paid +
      // rejected + expired) and the level filter for direct/others.
      // Sort discipline:
      //   direct  → state='pending' AND level=1, oldest first
      //   others  → state='pending' AND level>1, oldest first
      //   history → terminal states, newest first
      const r = await adminApi.get<{ rows: any[]; total: number }>(
        `${endpoint}?scope=${sub}&limit=${LIMIT}&offset=${offset}`
      );
      if (kind === 'recharge')              setRRows(r.rows as RechargeRow[]);
      else if (kind === 'withdrawal')       setWRows(r.rows as WithdrawalRow[]);
      else if (kind === 'agent-recharge')   setArRows(r.rows as AgentRechargeRow[]);
      else                                  setARows(r.rows as AgentWithdrawalRow[]);
      setTotal(r.total);
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setLoading(false); }
  }, [kind, sub, offset, endpoint]);

  useEffect(() => { setOffset(0); }, [kind, sub]);
  useEffect(() => { load(); }, [load]);

  // ── Actions ──

  // Modal state — drives partial-approval UI for all four actions.
  type ModalCtx =
    | { kind: 'recharge'; row: RechargeRow }
    | { kind: 'withdrawal'; row: WithdrawalRow }
    | { kind: 'agent-recharge'; row: AgentRechargeRow }
    | { kind: 'agent-withdrawal'; row: AgentWithdrawalRow }
    | null;
  const [modal, setModal] = useState<ModalCtx>(null);

  function approveRecharge(id: number) {
    const row = rRows.find(r => r.id === id);
    if (!row) return;
    setModal({ kind: 'recharge', row });
  }
  async function rejectRecharge(id: number) {
    const note = window.prompt('Rejection reason (optional):') ?? null;
    if (note === null) return;
    setBusyId(id); setErr(null);
    try {
      await adminApi.post(`/admin/payment-requests/recharge/${id}/reject`, note ? { note } : {});
      setToast('Rejected');
      await load(); onChanged();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusyId(null); }
  }
  function markWithdrawalPaid(row: WithdrawalRow | AgentWithdrawalRow, isAgent: boolean) {
    if (isAgent) setModal({ kind: 'agent-withdrawal', row: row as AgentWithdrawalRow });
    else         setModal({ kind: 'withdrawal',       row: row as WithdrawalRow });
  }
  async function rejectWithdrawal(id: number, isAgent: boolean) {
    const note = window.prompt(
      isAgent
        ? "Rejection reason (the agent's held balance will be refunded):"
        : "Rejection reason (the user's held balance will be refunded):"
    ) ?? null;
    if (note === null) return;
    setBusyId(id); setErr(null);
    try {
      const url = isAgent
        ? `/admin/payment-requests/agent-withdrawal/${id}/reject`
        : `/admin/payment-requests/withdrawal/${id}/reject`;
      await adminApi.post(url, note ? { note } : {});
      setToast('Rejected · held balance refunded');
      await load(); onChanged();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusyId(null); }
  }
  async function approveAgentWithdrawal(id: number) {
    setBusyId(id); setErr(null);
    try {
      await adminApi.post(`/admin/payment-requests/agent-withdrawal/${id}/approve`, {});
      setToast('Approved · ready to pay out');
      await load(); onChanged();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusyId(null); }
  }

  function approveAgentRecharge(id: number) {
    const row = arRows.find(r => r.id === id);
    if (!row) return;
    setModal({ kind: 'agent-recharge', row });
  }

  // Confirm handler shared across all four actions.
  async function onApprovalConfirm(args: { approvedAmount: number; note?: string; paidTransactionId?: string; payoutProofBlob?: Blob | null }) {
    if (!modal) return;
    const { approvedAmount, note, paidTransactionId, payoutProofBlob } = args;
    const id = modal.row.id;
    setBusyId(id); setErr(null);
    try {
      const body: Record<string, any> = { approved_amount: approvedAmount };
      if (note) body.note = note;
      let toastMsg = '';
      // The endpoint we'd POST a chained payout proof to. Set only
      // for withdrawal flows; null otherwise.
      let payoutProofUrlPath: string | null = null;
      if (modal.kind === 'recharge') {
        const r = await adminApi.post<{ bonus: { amount: number; ledgerId: number } | null }>(
          `/admin/payment-requests/recharge/${id}/approve`, body
        );
        toastMsg = r.bonus
          ? `Approved ${inr(approvedAmount)} · principal + ₹${r.bonus.amount} bonus`
          : `Approved ${inr(approvedAmount)}`;
      } else if (modal.kind === 'withdrawal') {
        body.paid_transaction_id = paidTransactionId!;
        await adminApi.post(`/admin/payment-requests/withdrawal/${id}/mark-paid`, body);
        toastMsg = `Marked paid ${inr(approvedAmount)}`;
        payoutProofUrlPath = `/admin/payment-requests/withdrawal/${id}/payout-proof`;
      } else if (modal.kind === 'agent-recharge') {
        await adminApi.post(`/admin/payment-requests/agent-recharge/${id}/approve`, body);
        toastMsg = `Approved ${inr(approvedAmount)} · pool credited`;
      } else { // agent-withdrawal
        body.paid_transaction_id = paidTransactionId!;
        await adminApi.post(`/admin/payment-requests/agent-withdrawal/${id}/mark-paid`, body);
        toastMsg = `Marked paid ${inr(approvedAmount)}`;
        payoutProofUrlPath = `/admin/payment-requests/agent-withdrawal/${id}/payout-proof`;
      }
      // Phase 18 — chain the payout-proof upload if attached. The
      // mark-paid action is the source of truth for the financial
      // state; if the screenshot upload fails for any reason we
      // surface a soft warning and the queue card's PayoutProofCell
      // shows the "Upload payout proof" button for the 24h window.
      if (payoutProofBlob && payoutProofUrlPath) {
        try {
          const fd = new FormData();
          fd.append('proof', payoutProofBlob, 'payout.jpg');
          await adminApi.postForm(payoutProofUrlPath, fd);
          toastMsg += ' · screenshot uploaded';
        } catch (uploadErr) {
          // Don't throw — the row is already paid. Surface the
          // mismatch as a banner so admin can retry from history.
          setErr('Marked paid, but screenshot upload failed. Retry from the queue card within 24 h.');
        }
      }
      setToast(toastMsg);
      setModal(null);
      await load(); onChanged();
    } catch (e) {
      setErr((e as ApiError).message || 'Failed');
      throw e;
    } finally {
      setBusyId(null);
    }
  }
  async function rejectAgentRecharge(id: number) {
    const note = window.prompt('Rejection reason (optional):') ?? null;
    if (note === null) return;
    setBusyId(id); setErr(null);
    try {
      await adminApi.post(`/admin/payment-requests/agent-recharge/${id}/reject`, note ? { note } : {});
      setToast('Rejected');
      await load(); onChanged();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusyId(null); }
  }

  // ── Render ──

  const rows: any[] = kind === 'recharge'         ? rRows
                    : kind === 'withdrawal'       ? wRows
                    : kind === 'agent-recharge'   ? arRows
                    :                                aRows;

  // Counterparty label for the approval modal.
  const modalRequested = modal?.row.amount ?? 0;
  const modalLabel = modal == null ? '' :
    modal.kind === 'recharge'        ? `@${(modal.row as RechargeRow).user_username}` :
    modal.kind === 'withdrawal'      ? `@${(modal.row as WithdrawalRow).user_username}` :
    modal.kind === 'agent-recharge'  ? `@${(modal.row as AgentRechargeRow).agent_username}` :
                                       `@${(modal.row as AgentWithdrawalRow).agent_username}`;
  const modalUiKind: 'recharge' | 'withdrawal' = (modal?.kind === 'withdrawal' || modal?.kind === 'agent-withdrawal')
    ? 'withdrawal' : 'recharge';

  return (
    <div className="px-2 lg:px-4 space-y-3">
      <ApprovalEditModal
        open={modal !== null}
        kind={modalUiKind}
        requestedAmount={modalRequested}
        counterpartyLabel={modalLabel}
        onCancel={() => setModal(null)}
        onConfirm={onApprovalConfirm}
      />
      {err && <Banner tone="error">{err}</Banner>}
      {toast && <Banner tone="success">{toast}</Banner>}

      {loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-8 text-center text-[13px] text-arena-600 dark:text-neon-200/70">
          Loading…
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-8 text-center">
          <Icon name="inbox" size={28} className="text-arena-400 dark:text-neon-200/35 mx-auto mb-2" />
          <div className="text-[13px] text-arena-600 dark:text-neon-200/70">
            {sub === 'direct'
              ? 'No pending L1 (direct) requests right now.'
              : sub === 'others'
              ? 'No pending Master / Agent requests right now.'
              : 'No history yet.'}
          </div>
        </div>
      )}

      <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {rows.map((row) => (
          <li key={row.id}>
            {kind === 'recharge'         && <RechargeCard        row={row} busy={busyId === row.id} onApprove={approveRecharge} onReject={rejectRecharge} />}
            {kind === 'withdrawal'       && <WithdrawalCard      row={row} busy={busyId === row.id} onMarkPaid={(r) => markWithdrawalPaid(r, false)} onReject={(id) => rejectWithdrawal(id, false)} onChanged={load} />}
            {kind === 'agent-recharge'   && <AgentRechargeCard   row={row} busy={busyId === row.id} onApprove={approveAgentRecharge} onReject={rejectAgentRecharge} />}
            {kind === 'agent-withdrawal' && <AgentWithdrawalCard row={row} busy={busyId === row.id} onApprove={approveAgentWithdrawal} onMarkPaid={(r) => markWithdrawalPaid(r, true)} onReject={(id) => rejectWithdrawal(id, true)} />}
          </li>
        ))}
      </ul>

      {total > LIMIT && (
        <div className="flex items-center justify-end gap-2 pt-2 text-[12px] text-arena-600 dark:text-neon-200/70">
          <button disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            className="grid place-items-center w-8 h-8 rounded-full bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60">
            <Icon name="chevron_left" size={16} />
          </button>
          <span className="tabular-nums">{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
          <button disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
            className="grid place-items-center w-8 h-8 rounded-full bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60">
            <Icon name="chevron_right" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Card components ───────────────────────────────────────────────

function CardShell({ children, accent = 'neutral' }: { children: ReactNode; accent?: 'in' | 'out' | 'neutral' }) {
  const ring = accent === 'in'
    ? 'ring-emerald-200 dark:ring-emerald-400/20'
    : accent === 'out'
      ? 'ring-rose-200 dark:ring-rose-400/20'
      : 'ring-neutral-200 dark:ring-white/10';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className={`
        rounded-2xl p-4
        bg-white dark:bg-arena-700/40
        ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 ring-1 ${ring}
        shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
      `}
    >
      {children}
    </motion.div>
  );
}

function FlowChip({ dir, amount }: { dir: 'in' | 'out'; amount: number }) {
  return (
    <span className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                      text-[14px] font-bold tabular-nums
                      ${dir === 'in'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                        : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'}`}>
      <Icon name={dir === 'in' ? 'arrow_upward' : 'arrow_downward'} size={14} />
      {inr(amount)}
    </span>
  );
}

function StatePill({ state }: { state: string }) {
  const M: Record<string, string> = {
    pending:  'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200/60 dark:bg-amber-400/10 dark:text-amber-200 dark:ring-amber-400/30',
    approved: 'bg-cyan-50 text-cyan-800 ring-1 ring-inset ring-cyan-200/60 dark:bg-cyan-400/10 dark:text-cyan-200 dark:ring-cyan-400/30',
    paid:     'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/10 dark:text-emerald-200 dark:ring-emerald-400/30',
    rejected: 'bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-400/10 dark:text-rose-200 dark:ring-rose-400/30',
    expired:  'bg-arena-100/60 text-arena-700 ring-1 ring-inset ring-arena-200/60 dark:bg-white/5 dark:text-white/60 dark:ring-white/10',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold capitalize ${M[state] || ''}`}>
      {state}
    </span>
  );
}

function ActionBtn({ tone, icon, label, onClick, busy }: {
  tone: 'success' | 'danger' | 'neutral'; icon: string; label: string; onClick: () => void; busy?: boolean;
}) {
  const M = {
    success: 'bg-emerald-600 text-white hover:bg-emerald-500 active:bg-emerald-700',
    danger:  'bg-white text-rose-700 ring-1 ring-inset ring-rose-200/60 hover:bg-rose-50 dark:bg-arena-700/40 dark:text-rose-300 dark:ring-rose-400/30 dark:hover:bg-rose-400/10',
    neutral: 'bg-white text-arena-700 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40 dark:hover:bg-arena-700/60',
  } as const;
  return (
    <button onClick={onClick} disabled={busy}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition disabled:opacity-50 active:scale-95 ${M[tone]}`}>
      <Icon name={busy ? 'progress_activity' : icon} size={14} className={busy ? 'animate-spin' : ''} />
      {label}
    </button>
  );
}

// "Owning agent" indicator on a player payment-request card. Tells
// the admin at a glance which agent's queue this row belongs to —
// shows the level (L1, L2, …) so admin instantly knows whether this
// row falls under Direct or Others.
//
// 2026-05-18 — Admin UI keeps the @username as a secondary identifier
// for ops (admin is allowed to see it). The display name takes the
// primary slot when available (composed from first/last); the
// @username sits next to it as a low-emphasis secondary ID. If both
// first/last are missing, only @username is shown — admins need at
// least one identifier to action a row.
function OwningAgentChip({
  agentId, agentUsername, agentFirstName, agentLastName, agentLevel,
}: {
  agentId: number | null;
  agentUsername: string | null;
  agentFirstName: string | null;
  agentLastName: string | null;
  agentLevel: number | null;
}) {
  if (!agentId) {
    // Post-2026-05-18 architecture: every player has an agent. This
    // branch should never fire for new rows. Legacy rows from before
    // the orphan-prevention deploy may still surface here.
    return (
      <span className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold
                       bg-arena-100/60 text-arena-700 dark:text-neon-200/80 ring-1 ring-inset ring-arena-200/60
                       dark:bg-white/5 dark:text-white/65 dark:ring-white/10">
        <Icon name="admin_panel_settings" size={10} /> Platform (legacy)
      </span>
    );
  }
  const name = displayName(agentFirstName, agentLastName);
  const hasName = name !== '—';
  return (
    <Link to={`/people/agents/${agentId}`}
          className="mt-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold
                     bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-200/60 hover:bg-violet-100
                     dark:bg-violet-400/10 dark:text-violet-200 dark:border-violet-400/30 dark:hover:bg-violet-400/15
                     transition">
      <Icon name="support_agent" size={10} />
      {agentLevel != null && (
        <span className="inline-flex items-center justify-center min-w-[18px] h-3.5 px-1 rounded-sm text-[9px] font-bold
                         bg-violet-200/70 text-violet-900 dark:bg-violet-300/20 dark:text-violet-100">
          {levelName(agentLevel)}
        </span>
      )}
      <span className="truncate max-w-[140px]">{hasName ? name : (agentUsername ? `@${agentUsername}` : `#${agentId}`)}</span>
      {hasName && agentUsername && (
        <span className="opacity-60 truncate max-w-[80px]">· @{agentUsername}</span>
      )}
    </Link>
  );
}

function RechargeCard({ row, busy, onApprove, onReject }: {
  row: RechargeRow; busy: boolean;
  onApprove: (id: number) => void; onReject: (id: number) => void;
}) {
  return (
    <CardShell accent="in">
      <div className="flex items-start gap-3 mb-3">
        <AdminAvatar url={null} username={row.user_username} size="sm" />
        {/* Phase C4 — Identity Stack. Admin always has full visibility:
            Line 1: First + Last (or — when missing). Line 2: @username
            (smaller). Line 3: phone (for contact). */}
        <div className="min-w-0 flex-1">
          <Link to={`/people/players/${row.user_id}`} className="text-[13.5px] font-semibold text-neutral-900 dark:text-white truncate hover:underline block">
            {(((row.user_first_name || "") + " " + (row.user_last_name || "")).trim() || "—")}
          </Link>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate font-mono">
            @{row.user_username} <span className="text-arena-400 dark:text-neon-200/40">·</span> #{row.user_id}
          </div>
          <div className="text-[11px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums">
            {row.user_phone
              ? <PhoneContact phone={row.user_phone} name={row.user_username} iconSize={11} className="tabular-nums" />
              : row.user_email}
          </div>
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/45 mt-0.5">{fmtDate(row.created_at)}</div>
          <OwningAgentChip
            agentId={row.target_agent_id}
            agentUsername={row.target_agent_username}
            agentFirstName={row.target_agent_first_name}
            agentLastName={row.target_agent_last_name}
            agentLevel={row.target_agent_level}
          />
        </div>
        {/* Phase 18 UI fix — proof affordance lives in the top-right
            column under the amount badge, maximally far from the
            bottom-right Approve button. Caught misclick risk in user
            review when the chip was sharing the action row. */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <FlowChip dir="in" amount={row.amount} />
          <ProofImageViewer
            state={proofStateFor(row.proof_uploaded_at, row.proof_image_url)}
            endpoint={`/admin/payment-requests/recharge/${row.id}/proof`}
            apiGet={(p) => adminApi.get(p)}
          />
        </div>
      </div>

      <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 px-3 py-2 mb-3 space-y-1">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-arena-600 dark:text-neon-200/70">UPI Txn ID</span>
          <span className="font-mono text-neutral-900 dark:text-white truncate">{row.transaction_id}</span>
        </div>
        {row.user_note && (
          <div className="text-[11px] text-arena-700 dark:text-neon-200/75 italic">"{row.user_note}"</div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatePill state={row.state} />
          {row.attached_offer_claim_id && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold
                             bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-200/60
                             dark:bg-violet-400/10 dark:text-violet-200 dark:border-violet-400/30">
              <Icon name="redeem" size={11} filled /> bonus attached
            </span>
          )}
          {row.ledger_id && (row.state === 'approved') && (
            <span className="text-[10.5px] text-arena-600 dark:text-neon-200/70">Ledger #{row.ledger_id}</span>
          )}
        </div>

        {row.state === 'pending' ? (
          <div className="flex items-center gap-2">
            <ActionBtn tone="danger"  icon="close" label="Reject"  onClick={() => onReject(row.id)}  busy={busy} />
            <ActionBtn tone="success" icon="check" label="Approve" onClick={() => onApprove(row.id)} busy={busy} />
          </div>
        ) : row.decision_note ? (
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/70 italic truncate max-w-[60%]">"{row.decision_note}"</div>
        ) : null}
      </div>
    </CardShell>
  );
}

function WithdrawalCard({ row, busy, onMarkPaid, onReject, onChanged }: {
  row: WithdrawalRow; busy: boolean;
  onMarkPaid: (row: WithdrawalRow) => void; onReject: (id: number) => void;
  onChanged?: () => void;
}) {
  return (
    <CardShell accent="out">
      <div className="flex items-start gap-3 mb-3">
        <AdminAvatar url={null} username={row.user_username} size="sm" />
        {/* Phase C4 — Identity Stack (Name / @username / phone). */}
        <div className="min-w-0 flex-1">
          <Link to={`/people/players/${row.user_id}`} className="text-[13.5px] font-semibold text-neutral-900 dark:text-white truncate hover:underline block">
            {(((row.user_first_name || "") + " " + (row.user_last_name || "")).trim() || "—")}
          </Link>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate font-mono">
            @{row.user_username} <span className="text-arena-400 dark:text-neon-200/40">·</span> #{row.user_id}
          </div>
          <div className="text-[11px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums">
            {row.user_phone
              ? <PhoneContact phone={row.user_phone} name={row.user_username} iconSize={11} className="tabular-nums" />
              : ''}
          </div>
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/45 mt-0.5">{fmtDate(row.created_at)}</div>
          <OwningAgentChip
            agentId={row.target_agent_id}
            agentUsername={row.target_agent_username}
            agentFirstName={row.target_agent_first_name}
            agentLastName={row.target_agent_last_name}
            agentLevel={row.target_agent_level}
          />
        </div>
        {/* Phase 18 UI fix — proof cell moved to the top-right column
            under the amount badge. Maximally far from the bottom-
            right Mark Paid button. */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <FlowChip dir="out" amount={row.amount} />
          <PayoutProofCell
            row={row}
            uploadEndpoint={`/admin/payment-requests/withdrawal/${row.id}/payout-proof`}
            viewEndpoint={`/admin/payment-requests/withdrawal/${row.id}/payout-proof`}
            onUploaded={() => onChanged?.()}
          />
        </div>
      </div>

      <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 px-3 py-2 mb-3 space-y-1">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-arena-600 dark:text-neon-200/70">Pay to (UPI)</span>
          <span className="font-mono text-neutral-900 dark:text-white truncate">{row.upi_id}</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {row.whatsapp_number && (
            <a href={`https://wa.me/${row.whatsapp_number.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
               className="text-[11px] inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 hover:underline">
              <Icon name="chat" size={11} /> WhatsApp
            </a>
          )}
          {row.qr_image_url && (
            <a href={row.qr_image_url} target="_blank" rel="noreferrer"
               className="text-[11px] inline-flex items-center gap-1 text-cyan-700 dark:text-cyan-300 hover:underline">
              <Icon name="qr_code_2" size={11} /> View QR
            </a>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatePill state={row.state} />
          {row.hold_ledger_id && (
            <span className="text-[10.5px] text-arena-600 dark:text-neon-200/70">Hold #{row.hold_ledger_id}</span>
          )}
          {row.paid_transaction_id && row.state === 'paid' && (
            <span className="text-[10.5px] text-emerald-700 dark:text-emerald-300 font-mono truncate">paid · {row.paid_transaction_id}</span>
          )}
        </div>

        {(row.state === 'pending' || row.state === 'approved') ? (
          <div className="flex items-center gap-2">
            <ActionBtn tone="danger"  icon="close"           label="Reject"    onClick={() => onReject(row.id)}    busy={busy} />
            <ActionBtn tone="success" icon="payments"        label="Mark paid" onClick={() => onMarkPaid(row)}     busy={busy} />
          </div>
        ) : row.decision_note ? (
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/70 italic truncate max-w-[60%]">"{row.decision_note}"</div>
        ) : null}
      </div>
    </CardShell>
  );
}

function AgentWithdrawalCard({ row, busy, onApprove, onMarkPaid, onReject }: {
  row: AgentWithdrawalRow; busy: boolean;
  onApprove: (id: number) => void;
  onMarkPaid: (row: AgentWithdrawalRow) => void;
  onReject: (id: number) => void;
}) {
  return (
    <CardShell accent="out">
      <div className="flex items-start gap-3 mb-3">
        <AdminAvatar url={row.agent_avatar_url} username={row.agent_username} size="sm" />
        <div className="min-w-0 flex-1">
          {/* 2026-05-18 — Admin-side card. Display name (composed from
              first/last) is the primary identifier; @username sits
              underneath as a secondary admin-ops handle. Level badge
              tells admin instantly whether this row falls under the
              Direct (L1) or Others (L2+) tab. */}
          <Link to={`/people/agents/${row.agent_id}`} className="text-[13.5px] font-semibold text-neutral-900 dark:text-white truncate hover:underline flex items-center gap-1.5">
            {displayName(row.agent_first_name, row.agent_last_name)}
            {row.agent_level != null && (
              <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-400/15 px-1.5 py-0.5 rounded">{levelName(row.agent_level)}</span>
            )}
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-400/15 px-1.5 py-0.5 rounded">agent</span>
          </Link>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate">
            @{row.agent_username} · {row.agent_email} · #{row.agent_id}
          </div>
          {row.agent_phone && (
            <div className="text-[11px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums mt-0.5">
              <PhoneContact phone={row.agent_phone} name={row.agent_username} iconSize={11} className="tabular-nums" />
            </div>
          )}
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/45 mt-0.5">{fmtDate(row.created_at)}</div>
        </div>
        {/* Phase 18 — payout proof on agent withdrawals (admin queue),
            top-right column, away from action buttons. */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <FlowChip dir="out" amount={row.amount} />
          <PayoutProofCell
            row={row}
            uploadEndpoint={`/admin/payment-requests/agent-withdrawal/${row.id}/payout-proof`}
            viewEndpoint={`/admin/payment-requests/agent-withdrawal/${row.id}/payout-proof`}
          />
        </div>
      </div>

      <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 px-3 py-2 mb-3 space-y-1">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-arena-600 dark:text-neon-200/70">Pay to (UPI)</span>
          <span className="font-mono text-neutral-900 dark:text-white truncate">{row.upi_id}</span>
        </div>
        {row.whatsapp_number && (
          <a href={`https://wa.me/${row.whatsapp_number.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
             className="text-[11px] inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 hover:underline">
            <Icon name="chat" size={11} /> WhatsApp {row.whatsapp_number}
          </a>
        )}
        {row.agent_note && (
          <div className="text-[11px] text-arena-700 dark:text-neon-200/75 italic">"{row.agent_note}"</div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatePill state={row.state} />
          {row.hold_ledger_id && (
            <span className="text-[10.5px] text-arena-600 dark:text-neon-200/70">Hold #{row.hold_ledger_id}</span>
          )}
          {row.paid_transaction_id && row.state === 'paid' && (
            <span className="text-[10.5px] text-emerald-700 dark:text-emerald-300 font-mono truncate">paid · {row.paid_transaction_id}</span>
          )}
        </div>

        {row.state === 'pending' && (
          <div className="flex items-center gap-2">
            <ActionBtn tone="danger"  icon="close" label="Reject"  onClick={() => onReject(row.id)}  busy={busy} />
            <ActionBtn tone="neutral" icon="check_circle" label="Approve" onClick={() => onApprove(row.id)} busy={busy} />
            <ActionBtn tone="success" icon="payments"     label="Pay now" onClick={() => onMarkPaid(row)}   busy={busy} />
          </div>
        )}
        {row.state === 'approved' && (
          <div className="flex items-center gap-2">
            <ActionBtn tone="danger"  icon="close"    label="Reject"   onClick={() => onReject(row.id)}  busy={busy} />
            <ActionBtn tone="success" icon="payments" label="Mark paid" onClick={() => onMarkPaid(row)}   busy={busy} />
          </div>
        )}
        {row.state !== 'pending' && row.state !== 'approved' && row.decision_note && (
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/70 italic truncate max-w-[60%]">"{row.decision_note}"</div>
        )}
      </div>
    </CardShell>
  );
}

function AgentRechargeCard({ row, busy, onApprove, onReject }: {
  row: AgentRechargeRow; busy: boolean;
  onApprove: (id: number) => void; onReject: (id: number) => void;
}) {
  return (
    <CardShell accent="in">
      <div className="flex items-start gap-3 mb-3">
        <AdminAvatar url={row.agent_avatar_url} username={row.agent_username} size="sm" />
        <div className="min-w-0 flex-1">
          {/* 2026-05-18 — Admin-side card; display_name primary, @username secondary, level badge for Direct/Others mapping. */}
          <Link to={`/people/agents/${row.agent_id}`} className="text-[13.5px] font-semibold text-neutral-900 dark:text-white truncate hover:underline flex items-center gap-1.5">
            {displayName(row.agent_first_name, row.agent_last_name)}
            {row.agent_level != null && (
              <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-400/15 px-1.5 py-0.5 rounded">{levelName(row.agent_level)}</span>
            )}
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-400/15 px-1.5 py-0.5 rounded">agent</span>
          </Link>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate">
            @{row.agent_username} · {row.agent_email} · #{row.agent_id}
          </div>
          {row.agent_phone && (
            <div className="text-[11px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums mt-0.5">
              <PhoneContact phone={row.agent_phone} name={row.agent_username} iconSize={11} className="tabular-nums" />
            </div>
          )}
          <div className="text-[10.5px] text-arena-500 dark:text-neon-200/45 mt-0.5">{fmtDate(row.created_at)}</div>
        </div>
        {/* Phase 18 — sender screenshot on agent recharges (admin queue),
            top-right column. */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <FlowChip dir="in" amount={row.amount} />
          <ProofImageViewer
            state={proofStateFor(row.proof_uploaded_at, row.proof_image_url)}
            endpoint={`/admin/payment-requests/agent-recharge/${row.id}/proof`}
            apiGet={(p) => adminApi.get(p)}
          />
        </div>
      </div>

      <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 px-3 py-2 mb-3 space-y-1">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-arena-600 dark:text-neon-200/70">UPI Txn ID</span>
          <span className="font-mono text-neutral-900 dark:text-white truncate">{row.transaction_id}</span>
        </div>
        {row.agent_note && (
          <div className="text-[11px] text-arena-700 dark:text-neon-200/75 italic">"{row.agent_note}"</div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatePill state={row.state} />
          {row.ledger_id && row.state === 'approved' && (
            <span className="text-[10.5px] text-arena-600 dark:text-neon-200/70">Ledger #{row.ledger_id}</span>
          )}
        </div>

        {row.state === 'pending' ? (
          <div className="flex items-center gap-2">
            <ActionBtn tone="danger"  icon="close" label="Reject"  onClick={() => onReject(row.id)}  busy={busy} />
            <ActionBtn tone="success" icon="check" label="Approve" onClick={() => onApprove(row.id)} busy={busy} />
          </div>
        ) : row.decision_note ? (
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/70 italic truncate max-w-[60%]">"{row.decision_note}"</div>
        ) : null}
      </div>
    </CardShell>
  );
}

// ─── Banner ────────────────────────────────────────────────────────

function Banner({ tone, children }: { tone: 'error' | 'success' | 'info'; children: ReactNode }) {
  const M = {
    error:   'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-200',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-200',
    info:    'bg-arena-50/60 ring-arena-200/60 text-arena-700 dark:bg-arena-800/40 dark:ring-arena-600/40 dark:text-neon-200/75',
  } as const;
  return (
    <div className={`rounded-2xl border px-4 py-3 text-[13px] ${M[tone]}`}>{children}</div>
  );
}
